"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.themeOptions = exports.theme = exports.darkTheme = exports.lightTheme = void 0;
var styles_1 = require("@mui/material/styles");
var palette_1 = require("./palette");
var darkPalette_1 = require("./darkPalette");
var typography_1 = require("./typography");
__exportStar(require("./themeMode"), exports);
__exportStar(require("./tableStyles"), exports);
__exportStar(require("./colors"), exports);
__exportStar(require("./calendarStyles"), exports);
__exportStar(require("./chartStyles"), exports);
// MUI's `alpha()` (used internally by Button/IconButton/etc. for hover and
// focus overlays) decomposes a real color at style-build time - it can't
// take a CSS var() reference. So dark mode is two fully precomputed theme
// objects (see TECH-1159 phase1.md), not a single theme whose palette
// values are swapped via CSS variables.
var buildTheme = function (paletteOptions) {
    var themeOptions = (0, styles_1.createTheme)({
        palette: __assign({}, paletteOptions),
        typography: __assign({}, typography_1.typography),
        shape: {
            borderRadius: 8,
        },
        spacing: 8,
    });
    var isDark = themeOptions.palette.mode === "dark";
    var darkPrimaryText = themeOptions.palette.primary.light;
    var buttonShadow = function (color) {
        return isDark ? "0px 4px 16px rgba(0, 0, 0, 0.6)" : "0px 4px 16px ".concat(color, "66");
    };
    return (0, styles_1.createTheme)(themeOptions, {
        components: {
            MuiButton: {
                styleOverrides: {
                    sizeLarge: __assign(__assign({}, themeOptions.typography.h5), { letterSpacing: 2 }),
                    sizeMedium: __assign(__assign({}, themeOptions.typography.subtitle1), { letterSpacing: 1.5 }),
                    sizeSmall: __assign(__assign({}, themeOptions.typography.overline), { letterSpacing: 1.5 }),
                    // eslint-disable-next-line
                    root: function (_a) {
                        var ownerState = _a.ownerState;
                        var paletteKey = ownerState.color;
                        var palette = themeOptions.palette[paletteKey];
                        return __assign(__assign({}, (ownerState.variant === "contained" && {
                            boxShadow: buttonShadow(palette.main),
                        })), (isDark &&
                            ownerState.color === "primary" &&
                            ownerState.variant !== "contained" && {
                            color: darkPrimaryText,
                        }));
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    // eslint-disable-next-line
                    root: function (_a) {
                        var ownerState = _a.ownerState;
                        if (isDark && ownerState.color === "primary.main") {
                            // `color` prop is applied via sx, which lands after styleOverrides
                            return { color: "".concat(darkPrimaryText, " !important") };
                        }
                        if (isDark && ownerState.color === "info.dark") {
                            // info.dark (#0B4361) is unreadable on the dark background
                            return {
                                color: "".concat(themeOptions.palette.info.light, " !important"),
                            };
                        }
                        return {};
                    },
                },
            },
            MuiFab: {
                styleOverrides: {
                    sizeLarge: __assign({}, themeOptions.typography.h5),
                    sizeMedium: __assign({}, themeOptions.typography.subtitle1),
                    sizeSmall: __assign({}, themeOptions.typography.overline),
                    // eslint-disable-next-line
                    root: function (_a) {
                        var ownerState = _a.ownerState;
                        var paletteKey = ownerState.color;
                        var palette = themeOptions.palette[paletteKey];
                        return __assign({
                            boxShadow: buttonShadow(palette.main),
                        });
                    },
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: themeOptions.palette.background.default + " !important",
                        ":before": {
                            borderBottom: "2px solid ".concat(themeOptions.palette.grey[300]),
                        },
                        ":hover": {
                            borderBottom: "2px solid ".concat(themeOptions.palette.grey[300]),
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        "& .MuiInputBase-input": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        "&.Mui-focused": {
                            fontWeight: 600,
                        },
                    },
                },
            },
            MuiGrid: {
                defaultProps: {
                    rowSpacing: 2,
                },
                styleOverrides: {
                    root: {
                        width: "100%",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        // padding: '16px',
                        // dark grey[500] is lighter than the dark background, so it would
                        // render as a pale halo - use a dark shadow instead
                        boxShadow: isDark
                            ? "0px 4px 16px rgba(0, 0, 0, 0.5)"
                            : "0px 4px 16px ".concat(themeOptions.palette.grey[500], "66"),
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        color: themeOptions.palette.grey[700],
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        padding: "8px 16px",
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        // drop dark mode's elevation overlay so drawers match background.paper
                        backgroundImage: "none",
                    },
                    // permanent/persistent left drawer = the shell navbar
                    paperAnchorDockedLeft: {
                        backgroundColor: themeOptions.palette.primary.main,
                    },
                },
            },
            MuiTable: {
                styleOverrides: {
                    root: {
                        padding: "0",
                    },
                },
            },
            MuiTableCell: {
                defaultProps: {
                    align: "center",
                },
                styleOverrides: {
                    root: {
                        color: "inherit",
                        textAlign: "center",
                        padding: 16,
                        borderBottom: "1px solid",
                        borderColor: themeOptions.palette.grey["200"] + " !important",
                        "&.MuiTableCell-head": {
                            backgroundColor: themeOptions.palette.grey["200"] + " !important",
                        },
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        color: themeOptions.palette.primary.main,
                        borderBottom: "none",
                        letterSpacing: 1,
                        backgroundColor: themeOptions.palette.grey["200"] + " !important",
                    },
                },
            },
            MuiTableSortLabel: {
                styleOverrides: {
                    icon: {
                        color: themeOptions.palette.primary.main,
                        fontWeight: 600,
                    },
                    iconDirectionAsc: {
                        color: themeOptions.palette.primary.main,
                        fontWeight: 600,
                    },
                    iconDirectionDesc: {
                        color: themeOptions.palette.primary.main,
                        fontWeight: 600,
                    },
                    root: {
                        color: themeOptions.palette.primary.main,
                        fontWeight: 600,
                        "&:hover, &.Mui-active": {
                            color: "".concat(themeOptions.palette.primary.dark, " !important"),
                        },
                        ".MuiTableSortLabel-icon": {
                            color: "inherit",
                        },
                    },
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    columnHeaders: {
                        backgroundColor: themeOptions.palette.primary.main,
                        color: themeOptions.palette.primary.contrastText,
                    },
                    columnHeaderTitle: {
                        fontWeight: "bold",
                    },
                    iconSeparator: {
                        color: themeOptions.palette.primary.contrastText,
                    },
                    sortIcon: {
                        color: themeOptions.palette.primary.contrastText,
                    },
                    menuIcon: {
                        color: themeOptions.palette.primary.contrastText,
                    },
                    menuIconButton: {
                        color: themeOptions.palette.primary.contrastText,
                    },
                    filterIcon: {
                        color: themeOptions.palette.primary.contrastText,
                    },
                },
            },
            MuiSnackbarContent: {
                styleOverrides: {
                    root: {
                        color: themeOptions.palette.grey["800"],
                        backgroundColor: themeOptions.palette.background.default,
                        indicator: {
                            backgroundColor: "".concat(themeOptions.palette.common.black, " !important"),
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: __assign({ root: {
                        color: themeOptions.palette.grey["900"],
                        fontWeight: 600,
                        padding: "8px 24px",
                    } }, (isDark && {
                    textColorPrimary: {
                        "&.Mui-selected": { color: darkPrimaryText },
                    },
                })),
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        backgroundColor: themeOptions.palette.background.paper,
                        padding: "8px 16px",
                        ".MuiTabs-indicator": {
                            backgroundColor: themeOptions.palette.secondary.main,
                        },
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    root: {
                        borderBottom: "1px solid",
                        borderColor: themeOptions.palette.grey["200"] + " !important",
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        "&$checked": {
                            color: themeOptions.palette.primary.main,
                        },
                    },
                    colorPrimary: {
                        "&$checked": {
                            color: themeOptions.palette.primary.main,
                        },
                    },
                    colorSecondary: {
                        "&$checked": {
                            color: themeOptions.palette.secondary.main,
                        },
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    colorPrimary: {
                        "&$checked": {
                            color: themeOptions.palette.primary.main,
                        },
                    },
                    colorSecondary: {
                        "&$checked": {
                            color: themeOptions.palette.secondary.main,
                        },
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    "*": {
                        boxSizing: "border-box",
                        margin: 0,
                        padding: 0,
                        "*::-webkit-scrollbar, &::-webkit-scrollbar-thumb, *::-webkit-scrollbar-track": {
                            border: "5px solid transparent",
                            backgroundClip: "padding-box",
                            transition: "all 1s ease",
                        },
                        "*::-webkit-scrollbar": {
                            width: "1.2rem",
                        },
                        "*::-webkit-scrollbar-track": {
                            backgroundColor: "transparent",
                        },
                        "*::-webkit-scrollbar-thumb": {
                            borderRadius: "2rem",
                            backgroundColor: themeOptions.palette.grey[500],
                        },
                        "*::-webkit-scrollbar:hover": {
                            borderRadius: "2rem",
                            backgroundColor: themeOptions.palette.grey[300],
                        },
                        "*::-webkit-scrollbar-thumb:hover": {
                            width: "2rem",
                            backgroundColor: themeOptions.palette.grey[700],
                        },
                    },
                    html: {
                        MozOsxFontSmoothing: "grayscale",
                        WebkitFontSmoothing: "antialiased",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: "100%",
                        width: "100%",
                        margin: 0,
                    },
                    body: {
                        display: "flex",
                        flex: "1 1 auto",
                        flexDirection: "column",
                        minHeight: "100%",
                        width: "100%",
                        margin: "0",
                        overflowX: "hidden",
                    },
                    "#__next": {
                        display: "flex",
                        flex: "1 1 auto",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                        margin: "0",
                    },
                },
            },
        },
    });
};
exports.lightTheme = buildTheme(palette_1.lightPalette);
exports.darkTheme = buildTheme(darkPalette_1.darkPalette);
// Back-compat: existing imports of `theme` (all 4 consuming apps, pre-dark-mode)
// keep working unchanged and render in light mode. Apps opting into dark mode
// should pick between `lightTheme`/`darkTheme` using `useThemeMode()` instead.
exports.theme = exports.lightTheme;
exports.themeOptions = exports.lightTheme;
//# sourceMappingURL=index.js.map